import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { min, max } from '../../../../theme/breakpoints';
import { FeatureItem } from './Feature/styles';

interface ISaveContainerProps {
  is12Months?: boolean;
}

interface IPlanCardProps {
  is12Months?: boolean;
  color?: string;
}

export const SaveContainer = styled.div<ISaveContainerProps>`
  position: absolute;
  right: 0;
  top: 0;
  transform: translate(20%, -40%);
  text-align: center;
  text-transform: uppercase;

  svg {
    display: block;

    @media screen and ${max('largeTablet')} {
      max-width: ${({ is12Months }) => (is12Months ? '100px' : '90px')};
      max-height: ${({ is12Months }) => (is12Months ? '100px' : '90px')};
    }
  }
`;

interface ISaveContentProps {
  is12Months: boolean;
  isZH: boolean;
}

export const SaveContent = styled.div<ISaveContentProps>`
  white-space: ${({ isZH }) => (!isZH ? 'nowrap' : 'normal')};
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: ${({ theme }) => theme.fonts.secondary.bold};
  color: ${({ theme }) => theme.colors.White};
  font-size: ${({ is12Months }) => (is12Months ? '1.0625rem' : '0.9375rem')};
  line-height: 1;
  margin-top: -0.5rem;

  @media screen and ${min('largeTablet')} {
    font-size: ${({ is12Months }) => (is12Months ? '1.5rem' : '1.25rem')};
  }
  strong {
    font-weight: inherit;
    font-size: ${({ is12Months }) => (is12Months ? '1.75rem' : '1.5rem')};

    @media screen and ${min('largeTablet')} {
      font-size: ${({ is12Months }) => (is12Months ? '2.375rem' : '2rem')};
    }
  }
`;

export const CardContentWrapper = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 5px;
  width: 100%;
  margin-top: 1.5rem;
  transition: 0.2s all ease-in-out;

  @media screen and ${max('desktop')} {
    height: 100%;
  }
`;

export const MainContentWrapper = styled(CardContentWrapper)``;

interface IShowPlanDetails {
  showPlanDetails: boolean;
}

export const FeaturesContainer = styled.div<IShowPlanDetails>`
  @media screen and ${max('desktop')} {
    display: ${({ showPlanDetails }) => (showPlanDetails ? 'block' : 'none')};
  }
`;

export const SecondaryContentWrapper = styled(CardContentWrapper)<IShowPlanDetails>`
  @media screen and ${max('desktop')} {
    display: ${({ showPlanDetails }) => (showPlanDetails ? 'block' : 'none')};
  }
`;

export const PlanCardWrapper = styled(motion.div)<IPlanCardProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-basis: 33.333%;
  padding: 1.25rem 0;

  @media screen and ${min('tablet')} {
    padding: 1rem;
  }
  @media screen and ${min('largeTablet')} {
    padding: 1.25rem;

    &:hover {
      ${CardContentWrapper} {
        transform: translateY(-2px);
        box-shadow: 6px 12px 12px rgba(0, 0, 0, 0.25);

        ${({ is12Months }) =>
          is12Months &&
          css`
            box-shadow: 20px 16px 16px rgba(0, 0, 0, 0.25);
          `};
      }
    }
  }
  @media screen and ${max('largeTablet')} {
    flex-basis: 50%;
    align-items: center;
  }
  @media screen and ${max('tablet')} {
    flex-basis: unset;
    align-items: unset;
  }
  ${FeatureItem} {
    span {
      color: ${({ color }) => color};
      font-family: ${({ theme }) => theme.fonts.primary.bold};
    }
  }
  ${MainContentWrapper},
  ${SecondaryContentWrapper} {
    ${({ is12Months }) =>
      is12Months &&
      css`
        box-shadow: 12px 16px 16px rgba(0, 0, 0, 0.25);

        ${SaveContent} {
          margin-left: 0.25rem;
        }
      `};
  }
  ${MainContentWrapper} {
    ${({ is12Months, color }) =>
      is12Months &&
      color &&
      css`
        margin-top: 0;
        border-top: 1.5rem solid ${color};
      `};
  }
`;

interface IContentProps {
  isNot1Month?: boolean;
}

export const Content = styled.div<IContentProps>`
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media screen and ${max('largeDesktop')} {
    padding: 20px;
  }
  .subscribe-plans-ctas {
    margin: 1.5625rem 0;
    ${({ isNot1Month }) => isNot1Month && 'margin-top: 1.5rem;'};
  }
  > p {
    color: ${({ theme }) => theme.colors.text};
    margin: 0;
    font-size: 0.875rem;
  }
  > a {
    width: 100%;
  }
`;

export const HeaderSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  > h3 {
    text-align: center;
    padding: 0;
    margin: 0;
  }
  > p,
  strong {
    font-family: ${({ theme }) => theme.fonts.primary.bold};
  }
  strong {
    margin-bottom: 1rem;
  }
  > span {
    margin-top: 0.25rem;
    font-size: 0.875rem;

    @media screen and ${min('largeTablet')} {
      font-size: 1.125rem;
    }
  }
`;

interface IDuration {
  color?: string;
}

export const Duration = styled.strong<IDuration>`
  font-size: 1.5rem;
  text-transform: uppercase;
  color: ${({ color, theme }) => color ?? theme.colors.text};
`;

export const PlatformSection = styled.div`
  display: flex;
  align-items: baseline;
  font-size: 1.125rem;

  > p {
    color: ${({ theme }) => theme.colors.text};
    margin-right: 1rem;
  }
  > span {
    border: 1px solid ${({ theme }) => theme.colors.text};
    padding: 0 7px;
  }
`;

interface IFeaturesSection {
  padding?: number;
}

export const FeaturesSection = styled.ul<IFeaturesSection>`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: ${({ padding }) => (padding ? 'center' : 'flex-start')};
  padding: ${({ padding }) => `${(padding ?? 1) - 1}rem ${padding}rem`};
`;

export const UbiplusSection = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.colors.text};
  margin-top: 1.5625rem;

  > p {
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.125rem;
    margin: 0;
  }
`;

export const UbiplusIconSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 1.5625rem 0 1rem 0;

  > a {
    font-family: ${({ theme }) => theme.fonts.primary.regular};
    color: ${({ theme }) => theme.colors.text};
    font-size: 1rem;
    text-decoration: underline;
    transition: 0.2s all ease-in-out;

    @media screen and ${max('largeDesktop')} {
      font-size: 0.85rem;
    }
    @media screen and ${min('largeTablet')} {
      &:hover {
        opacity: 0.5;
      }
    }
  }
  svg * {
    fill: ${({ theme }) => theme.colors.text};
  }
`;

export const BonusTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  background-color: ${({ theme }) => (theme.name === 'uplay' ? theme.colors.Mako : theme.colors.Mystic)};
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;

  > span {
    font-family: ${({ theme }) => theme.fonts.primary.bold};
    font-size: 1rem;
    margin-left: 5px;
  }
  svg * {
    fill: ${({ theme }) => theme.colors.text};
  }
`;

interface IPlanDetails {
  isOpen?: boolean;
}

export const PlanDetails = styled.button<IPlanDetails>`
  display: flex;
  justify-content: center;
  align-items: baseline;
  text-decoration: underline;
  border: 0;
  background: transparent;
  appearance: none;

  @media screen and ${min('desktop')} {
    display: none;
  }
  > svg {
    margin-left: 0.5rem;
    transition-duration: 0.25s;
    transition-property: transform;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(0deg)' : 'rotate(180deg)')};

    path {
      fill: ${({ theme }) => theme.colors.text};
    }
  }
`;

export const Price = styled.p`
  margin: 0;
  font-size: 1rem;

  @media screen and ${min('largeTablet')} {
    font-size: 1.25rem;
  }
  span {
    font-size: 1.375rem;

    @media screen and ${min('largeTablet')} {
      font-size: 1.75rem;
    }
  }
`;

export const FreeTrialNotice = styled.span`
  font-size: 0.875rem !important;
  font-family: ${({ theme }) => theme.fonts.primary.bold};
  background-color: ${({ theme }) => theme.colors.text};
  color: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
  padding: 0.3125rem 2rem;
  margin-top: 0;
  margin-bottom: 0.5rem;

  @media screen and ${min('largeTablet')} {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    border-radius: 5px;
    padding: 0.3125rem 0.625rem;
    margin-bottom: 0;
  }
`;

export const ButtonLoaderContainer = styled.div`
  margin: 1.5rem 0;
  height: 2.75rem;
  display: flex;
  align-items: center;
`;
